'use client'

import { useDeviceType } from '@/hooks/use-device-type'
import { useMarketingConsent } from '@/hooks/use-marketing-consent'
import {
  registerAdSlot,
  registerAnchorAdSlot,
} from '@/utils/ads/ad-slot-manager'
import type {
  AdSlot,
  AdType,
  AnchorAdSlot,
  AnchorFormat,
} from '@/utils/ads/types'
import cx from 'classnames'
import { usePathname } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'
import s from './google-ad-client.module.css'

export type { AdSlot, AdType, AnchorAdSlot, AnchorFormat }

type Props = {
  mobileAd?: AdSlot
  desktopAd?: AdSlot
  anchorAd?: AnchorAdSlot
  fallback?: React.ReactNode
  targetKey?: string
  onAdsLoaded?: (adType: AdType) => void
}

type RenderStatus = 'pending' | 'filled' | 'empty'

type RenderState = {
  registrationKey: string
  status: Exclude<RenderStatus, 'pending'>
}

const ADSENSE_SRC =
  'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4242266867128351'

export const GoogleAdClient = ({
  mobileAd,
  desktopAd,
  anchorAd,
  fallback,
  targetKey,
  onAdsLoaded,
}: Props) => {
  const pathname = usePathname()
  const deviceType = useDeviceType()
  const consent = useMarketingConsent()
  // GPT answers every fetch with slotRenderEnded, so a slot is in one of three
  // states: waiting for the response, filled, or explicitly empty. The state
  // is keyed by the registration it answers, so a device switch, a consent
  // change or new targeting starts over from pending and the placeholder
  // reserves the space again until GPT answers.
  const [renderState, setRenderState] = useState<RenderState | null>(null)

  // Watched by the manager's IntersectionObserver. It has to be the element
  // that is laid out while the slot waits to be fetched, so it cannot be the
  // GPT div, which sits inside a display:none wrapper until the ad renders.
  const observedRef = useRef<HTMLDivElement>(null)

  const onAdsLoadedRef = useRef(onAdsLoaded)
  useEffect(() => {
    onAdsLoadedRef.current = onAdsLoaded
  })

  // The container and the slot definition read one device value, so defining a
  // slot without a container on the page is not possible.
  const activeAd = deviceType === 'desktop' ? desktopAd : mobileAd
  const adType: AdType = deviceType === 'desktop' ? 'desktop' : 'mobile'

  const adUnit = activeAd?.adUnit
  const divId = activeAd?.divId
  const width = activeAd?.size[0]
  const height = activeAd?.size[1]

  const targetValue = pathname?.split('/').filter(Boolean).pop() || ''

  // Mirrors the dependency list of the registration effect below.
  const registrationKey =
    adUnit && divId
      ? [adUnit, divId, deviceType, targetKey ?? '', targetValue, consent].join(
          '|'
        )
      : null
  const renderStatus: RenderStatus =
    registrationKey !== null && renderState?.registrationKey === registrationKey
      ? renderState.status
      : 'pending'
  const isFilled = renderStatus === 'filled'
  const isEmpty = renderStatus === 'empty'

  const anchorAdUnit = anchorAd?.adUnit
  const anchorFormat = anchorAd?.format

  useEffect(() => {
    const element = observedRef.current

    if (!registrationKey || !adUnit || !divId || !width || !height || !element)
      return

    const unregister = registerAdSlot({
      adUnit,
      size: [width, height],
      divId,
      deviceType,
      element,
      targetKey,
      targetValue,
      onRenderEnded: (isEmpty) => {
        setRenderState({
          registrationKey,
          status: isEmpty ? 'empty' : 'filled',
        })
        if (!isEmpty) onAdsLoadedRef.current?.(adType)
      },
    })

    return () => {
      unregister()
      // The slot is destroyed with the registration, so its last response no
      // longer describes anything on the page. Going back to pending puts the
      // placeholder up again, which is what the IntersectionObserver watches
      // to trigger the next fetch. Without this, revoking and re-granting
      // consent lands on the same key as before and the slot never refetches.
      setRenderState(null)
    }
  }, [
    registrationKey,
    adUnit,
    divId,
    width,
    height,
    deviceType,
    adType,
    targetKey,
    targetValue,
  ])

  useEffect(() => {
    if (!anchorAdUnit || !anchorFormat) return

    return registerAnchorAdSlot({
      adUnit: anchorAdUnit,
      format: anchorFormat,
      targetKey,
      targetValue,
      onDisplayed: () => onAdsLoadedRef.current?.('anchor'),
    })
  }, [anchorAdUnit, anchorFormat, targetKey, targetValue, consent])

  useEffect(() => {
    if (consent !== 'granted') return
    if (document.querySelector(`script[src="${ADSENSE_SRC}"]`)) return

    const script = document.createElement('script')
    script.src = ADSENSE_SRC
    script.async = true
    script.crossOrigin = 'anonymous'
    document.head.appendChild(script)
  }, [consent])

  if (!activeAd) return null

  const adsBlocked = consent === 'denied'

  const reservedSize = {
    minWidth: activeAd.size[0],
    minHeight: activeAd.size[1],
    width: activeAd.size[0],
    height: activeAd.size[1],
  }

  return (
    <>
      <div
        className={cx(s.adWrapper, { [s.hide]: !isFilled })}
        style={reservedSize}
      >
        <div id={activeAd.divId} {...activeAd.divProps} />
      </div>

      {fallback ? (
        // The fallback fills the space whenever there is no ad to show, so it
        // stays up on an empty response.
        <div
          ref={observedRef}
          className={cx(s.fallbackWrapper, { [s.hide]: isFilled })}
        >
          {fallback}
        </div>
      ) : adsBlocked ? null : (
        // The placeholder only reserves the space while the response is
        // pending. Once GPT says the slot is empty, leaving it up would hold
        // a blank box in the layout at the full ad size.
        <div
          ref={observedRef}
          className={cx(s.adPlaceholder, { [s.hide]: isFilled || isEmpty })}
          style={reservedSize}
        />
      )}
    </>
  )
}
